import React from 'react';
import { Bed, Waves, Users } from 'lucide-react';
import { dobStatus, maskDob, legOk, legWeeks, piscineForWeek, fmtEuro } from './devisCalc';

const Y = 'bg-[#FFE066] border-2 border-[#F5C400] text-[#1E3A5F] font-semibold rounded-lg';
const G = 'bg-gray-200 text-gray-700 font-semibold rounded-lg text-center';

const Statut = ({ calc, pos, idx, sfx = '' }) => {
  const l = calc.lines.find((x) => x.pos === pos);
  if (!l) return <span className={`${G} inline-block px-2 py-2 text-xs w-full`}>—</span>;
  return <span className={`${G} inline-block px-2 py-2 text-xs w-full leading-tight`} data-testid={`statut-${idx}-${pos}${sfx}`}>{l.statut}</span>;
};

const Leg = ({ leg, wk, trav, onChange, testid }) => {
  if (!legOk(wk, leg)) return <span className={`${G} inline-block px-2 py-2 text-xs w-full`} title="Pas de car de l'association sur cette date">Pas de car</span>;
  return (
    <select value={trav[leg] ? 'car' : 'non'} onChange={(e) => onChange({ [leg]: e.target.value === 'car' })} className={`${Y} w-full px-2 py-2 text-sm`} data-testid={testid}>
      <option value="car">Car — {wk.prixTrajet} €</option>
      <option value="non">Par mes moyens</option>
    </select>
  );
};

export const DevisChambre = ({ room, idx, weeks, calc, onRoom, onTraveler }) => {
  const start = weeks[0]?.start;
  const lw = legWeeks(weeks);
  const travs = room.travelers.slice(0, room.nb);
  const piscineMontant = weeks.reduce((s, w) => s + piscineForWeek(w), 0);
  const dobCell = (t, i, sfx = '') => {
    const st = dobStatus(t.dob, start);
    return (
      <div>
        <input type="text" inputMode="numeric" placeholder="JJ/MM/AAAA" value={t.dob} onChange={(e) => onTraveler(i, { dob: maskDob(e.target.value) })}
          className={`${Y} w-full px-3 py-2 text-sm placeholder:text-[#1E3A5F]/40 ${st.error ? 'border-red-500' : ''}`} data-testid={`dob-${idx}-${i}${sfx}`} aria-label={`Date de naissance voyageur ${i + 1}`} />
        {st.error && <p className="text-[11px] text-red-600 mt-1 font-semibold" data-testid={`dob-err-${idx}-${i}${sfx}`}>{st.error}</p>}
      </div>
    );
  };
  const ageCell = (t, i, sfx = '') => { const st = dobStatus(t.dob, start); return <span className={`${G} inline-block px-2 py-2 text-sm w-full`} data-testid={`age-${idx}-${i}${sfx}`}>{st.age ?? '—'}</span>; };

  return (
    <div data-testid={`chambre-${idx + 1}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-[#1E3A5F]"><Users size={16} /> Nombre de voyageurs
          <select value={room.nb} onChange={(e) => onRoom({ nb: Number(e.target.value) })} className={`${Y} px-3 py-1.5`} data-testid={`nb-${idx}`}>
            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        {calc.adults > 3 && <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-300 rounded-lg px-2 py-1" data-testid={`adultes-err-${idx}`}>Maximum 3 adultes par chambre — 4 adultes = 2 chambres</span>}
      </div>

      {/* Tableau (tablette / ordinateur) */}
      <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-[#EEF3F8] text-[#1E3A5F]">
            <tr>{['Position', 'Date de naissance (JJ/MM/AAAA)', 'Âge', 'Statut', 'Transport aller', 'Transport retour'].map((h) => <th key={h} className="px-3 py-3 text-left font-bold text-xs">{h}</th>)}</tr>
          </thead>
          <tbody>
            {travs.map((t, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-3 py-2 font-semibold text-[#1E3A5F] whitespace-nowrap">Voyageur {i + 1}</td>
                <td className="px-3 py-2 w-48">{dobCell(t, i)}</td>
                <td className="px-3 py-2 w-16">{ageCell(t, i)}</td>
                <td className="px-3 py-2 w-44"><Statut calc={calc} pos={i + 1} idx={idx} /></td>
                <td className="px-3 py-2 w-40"><Leg leg="aller" wk={lw.aller} trav={t} onChange={(p) => onTraveler(i, p)} testid={`aller-${idx}-${i}`} /></td>
                <td className="px-3 py-2 w-40"><Leg leg="retour" wk={lw.retour} trav={t} onChange={(p) => onTraveler(i, p)} testid={`retour-${idx}-${i}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cartes (téléphone) */}
      <div className="md:hidden space-y-3">
        {travs.map((t, i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-3 bg-white" data-testid={`carte-${idx}-${i}`}>
            <p className="font-bold text-[#1E3A5F] mb-2">Voyageur {i + 1}</p>
            <label className="text-xs text-gray-500 block mb-1">Date de naissance (JJ/MM/AAAA)</label>
            {dobCell(t, i, '-m')}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><span className="text-xs text-gray-500 block mb-1">Âge</span>{ageCell(t, i, '-m')}</div>
              <div><span className="text-xs text-gray-500 block mb-1">Statut</span><Statut calc={calc} pos={i + 1} idx={idx} sfx="-m" /></div>
              <div><span className="text-xs text-gray-500 block mb-1">Transport aller</span><Leg leg="aller" wk={lw.aller} trav={t} onChange={(p) => onTraveler(i, p)} testid={`aller-${idx}-${i}-m`} /></div>
              <div><span className="text-xs text-gray-500 block mb-1">Transport retour</span><Leg leg="retour" wk={lw.retour} trav={t} onChange={(p) => onTraveler(i, p)} testid={`retour-${idx}-${i}-m`} /></div>
            </div>
          </div>
        ))}
      </div>

      {calc.complete && calc.isSingle && (
        <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`single-${idx}`}>
          Supplément chambre individuelle : <strong>+{calc.supplement} €</strong>{calc.optimized ? ' — appliqué car plus avantageux que 2 pleins tarifs.' : ''}
        </p>
      )}

      {/* Options de la chambre */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="rounded-xl border-2 border-[#1E3A5F]/15 p-4" data-testid={`literie-${idx}`}>
          <p className="font-bold text-[#1E3A5F] flex items-center gap-2 mb-2"><Bed size={18} /> LITERIE <span className="text-xs font-normal text-gray-500">— sans incidence sur le prix</span></p>
          {room.nb === 1 ? (
            <p className="text-sm text-gray-700 rounded-lg px-3 py-2 border border-gray-200 bg-gray-50" data-testid={`literie-single-${idx}`}>
              Chambre individuelle : <strong>un lit simple</strong> (pas de grand lit).
            </p>
          ) : (
          <div className="space-y-2">
            {[['', 'Sans préférence'], ['separes', 'Deux lits séparés'], ['couple', 'Configuration couple : grand lit souhaité (non garanti), deux lits simples rapprochés possibles']].map(([v, l]) => (
              <label key={v} className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 cursor-pointer border ${room.literie === v ? 'border-[#1E3A5F] bg-[#EEF3F8]' : 'border-gray-200'}`}>
                <input type="radio" name={`literie-${idx}`} checked={room.literie === v} onChange={() => onRoom({ literie: v })} className="mt-0.5 accent-[#1E3A5F]" data-testid={`literie-${idx}-${v || 'aucune'}`} />
                <span className="text-[#1E3A5F]">{l}</span>
              </label>
            ))}
          </div>
          )}
          {room.nb > 1 && <p className="text-[11px] text-gray-600 mt-2 italic">Le grand lit est une préférence, non une garantie. Pour les couples, la chambre peut être aménagée avec deux lits simples rapprochés, selon les disponibilités de l'hôtel.</p>}
        </div>

        <div className="rounded-xl border-2 border-[#1E3A5F]/15 p-4" data-testid={`piscine-${idx}`}>
          <p className="font-bold text-[#1E3A5F] flex items-center gap-2"><Waves size={18} /> VUE PISCINE GARANTIE</p>
          <p className="text-xs text-gray-500 mb-3">Supplément par chambre pour l'ensemble du séjour</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => onRoom({ piscine: true })} className={`py-3 rounded-lg font-bold text-sm border-2 transition-colors ${room.piscine ? 'bg-[#D62828] border-[#D62828] text-white' : 'bg-white border-gray-300 text-gray-600'}`} data-testid={`piscine-${idx}-oui`} aria-pressed={room.piscine}>OUI</button>
            <button type="button" onClick={() => onRoom({ piscine: false })} className={`py-3 rounded-lg font-bold text-sm border-2 transition-colors ${!room.piscine ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white' : 'bg-white border-gray-300 text-gray-600'}`} data-testid={`piscine-${idx}-non`} aria-pressed={!room.piscine}>NON</button>
          </div>
          <p className="text-sm font-semibold text-[#1E3A5F] mt-3" data-testid={`piscine-montant-${idx}`}>+{fmtEuro(piscineMontant)} pour cette chambre et pour tout le séjour{room.piscine ? ' — ajouté au devis' : ''}</p>
        </div>
      </div>
    </div>
  );
};
